// Sign-in UI for free accounts (passwordless email link; see backend/src/api/auth.js).
// Signed out: follows live in this browser only. Signed in: the follows from
// this browser are merged with the account's and kept in sync, so they appear
// on every device where the visitor signs in.

import { getFollows, setFollows, onFollowsChange, toggleCountry } from './follow.js';
import { t, lang, countryName } from '../i18n/index.js';

let me = null;
let btn;
let dialog;
let syncTimer;

const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

async function api(path, options = {}) {
    const res = await fetch(`/api${path}`, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status });
    return data;
}

function renderButton() {
    if (!btn) return;
    const label = me ? t('auth.account') : t('auth.signIn');
    btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/></svg><span class="auth-label">${esc(label)}</span>`;
    btn.setAttribute('aria-label', label);
    btn.title = me ? me.email : t('auth.btnTitle');
}

function openDialog(html) {
    dialog.innerHTML = `<div class="au-box" role="dialog" aria-modal="true" aria-label="Account">
        <button type="button" class="au-close" aria-label="${esc(t('close'))}">×</button>${html}</div>`;
    dialog.classList.add('open');
    dialog.querySelector('.au-close').addEventListener('click', closeDialog);
}

function closeDialog() {
    dialog.classList.remove('open');
}

function showSignIn(message = '') {
    openDialog(`
        <h2>${esc(t('auth.freeTitle'))}</h2>
        <p class="au-sub">${esc(t('auth.freeSub'))}</p>
        <form class="au-form">
            <input type="email" name="email" required placeholder="you@example.com" autocomplete="email" aria-label="Email" />
            <button type="submit">${esc(t('auth.emailBtn'))}</button>
        </form>
        <p class="au-msg" role="status">${esc(message)}</p>
        <p class="au-fine">${esc(t('auth.fine')).replace('{link}', `<a href="https://newsnetwide.com/privacy" target="_blank" rel="noopener">${esc(t('auth.privacy'))}</a>`)}</p>`);
    const form = dialog.querySelector('.au-form');
    const msg = dialog.querySelector('.au-msg');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const b = form.querySelector('button');
        b.disabled = true;
        msg.textContent = t('auth.sending');
        try {
            const email = form.email.value.trim();
            const r = await api('/auth/request', { method: 'POST', body: JSON.stringify({ email, lang }) });
            showCode(email, r);
        } catch (err) {
            msg.textContent = err.message;
            b.disabled = false;
        }
    });
}

// Step 2: type the 6-digit code from the email on this device (the link in the email signs in
// whichever device opens it, often the phone, so the code is what works on a computer).
function showCode(email, r = {}) {
    openDialog(`
        <h2>${esc(t('auth.codeTitle'))}</h2>
        <p class="au-sub">${esc(t('auth.codeSent', { email }))}</p>
        <form class="au-form au-code-form">
            <input name="code" required inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6"
                placeholder="123456" aria-label="${esc(t('auth.codeLabel'))}" class="au-code" />
            <button type="submit">${esc(t('auth.codeBtn'))}</button>
        </form>
        <p class="au-msg" role="status">${r.dev_code ? `${esc(t('auth.dev'))} ${esc(r.dev_code)} · <a href="${esc(r.dev_link)}">${esc(t('auth.devLink'))}</a>` : ''}</p>
        <p class="au-junk">${esc(t('auth.junk'))}</p>
        <p class="au-fine"><a href="#" data-other>${esc(t('auth.otherEmail'))}</a></p>`);
    const form = dialog.querySelector('.au-code-form');
    const msg = dialog.querySelector('.au-msg');
    const input = form.code;
    input.focus();
    input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        if (input.value.length === 6) form.requestSubmit();
    });
    dialog.querySelector('[data-other]').addEventListener('click', (e) => {
        e.preventDefault();
        showSignIn();
    });
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const b = form.querySelector('button');
        if (b.disabled) return;
        b.disabled = true;
        msg.textContent = t('auth.sending');
        try {
            await api('/auth/code', { method: 'POST', body: JSON.stringify({ email, code: input.value }) });
            // Back to the page where they signed in (the globe, the calendar…)
            location.href = `${location.pathname}?signin=ok`;
        } catch (err) {
            msg.textContent = err.message;
            b.disabled = false;
            input.select();
        }
    });
}

// Account dashboard: who you are, the countries you follow, email settings,
// sign out and delete. Opened from the top-right Account button.
function showAccount() {
    const since = me.since ? new Date(`${String(me.since).replace(' ', 'T')}Z`) : null;
    const sinceText = since && !isNaN(since) ? since.toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    openDialog(`
        <h2>${esc(t('auth.yourAccount'))}</h2>
        <p class="au-sub">${esc(t('auth.signedInAs', { email: me.email }))}${sinceText ? `<br>${esc(t('auth.since', { date: sinceText }))}` : ''}</p>


        <h3 class="au-h">${esc(t('auth.followed'))}</h3>
        <div class="au-chips" data-chips></div>

        <div class="au-email" data-briefing hidden>
            <h3 class="au-h">${esc(t('auth.briefing'))}</h3>
            <label class="au-toggle"><input type="checkbox" data-brief-on>
                <span><b>${esc(t('auth.briefingOn'))}</b><small>${esc(t('auth.briefingSub'))}</small></span></label>
            <div class="au-brief-row" data-brief-time>
                <label>${esc(t('auth.briefingAt'))} <select data-brief-hour aria-label="${esc(t('auth.briefingAt'))}"></select></label>
                <small data-brief-tz></small>
            </div>
            <p class="au-brief-note" data-brief-note></p>
            <a class="au-brief-preview" href="/api/me/briefing/preview" target="_blank" rel="noopener">${esc(t('auth.briefingPreview'))}</a>
        </div>

        <div class="au-email" data-email hidden>
            <h3 class="au-h">${esc(t('auth.email'))}</h3>
            <label class="au-toggle"><input type="checkbox" data-pref="updates">
                <span><b>${esc(t('auth.prefUpdates'))}</b><small>${esc(t('auth.prefUpdatesSub'))}</small></span></label>
            <label class="au-toggle"><input type="checkbox" data-pref="newsletter">
                <span><b>${esc(t('auth.prefNews'))}</b><small>${esc(t('auth.prefNewsSub'))}</small></span></label>
        </div>

        <div class="au-row">
            <button type="button" class="au-logout">${esc(t('auth.signOut'))}</button>
            <button type="button" class="au-delete">${esc(t('auth.delete'))}</button>
        </div>
        <p class="au-msg" role="status"></p>
        <p class="au-fine"><a href="https://newsnetwide.com/privacy" target="_blank" rel="noopener">${esc(t('auth.privacy'))}</a></p>`);

    const msg = dialog.querySelector('.au-msg');

    // Followed countries, each removable with one tap.
    const chips = dialog.querySelector('[data-chips]');
    let names = null;
    try {
        names = new Intl.DisplayNames([lang, 'en'], { type: 'region' });
    } catch {
        /* older browsers: show codes */
    }
    const nameOf = (c) => countryName(c, names?.of(c) || c);
    const drawChips = () => {
        const list = getFollows().countries;
        chips.innerHTML = list.length
            ? list
                  .map(
                      (c) => `<button type="button" class="au-chip" data-iso2="${esc(c)}" aria-label="${esc(t('auth.unfollow', { country: nameOf(c) }))}">
                          ${esc(nameOf(c))} <span aria-hidden="true">×</span></button>`,
                  )
                  .join('')
            : `<p class="au-sub">${esc(t('auth.noneFollowed'))}</p>`;
    };
    drawChips();
    chips.addEventListener('click', (e) => {
        const chip = e.target.closest('.au-chip');
        if (!chip) return;
        toggleCountry(chip.dataset.iso2); // syncs to the account via onFollowsChange
        drawChips();
    });

    // Morning briefing (hidden until the server can send email).
    const brief = dialog.querySelector('[data-briefing]');
    const bOn = brief.querySelector('[data-brief-on]');
    const bHour = brief.querySelector('[data-brief-hour]');
    const bNote = brief.querySelector('[data-brief-note]');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Malta';
    brief.querySelector('[data-brief-tz]').textContent = t('auth.briefingTz', { tz: tz.replace(/_/g, ' ') });
    const hourLabel = (h) => new Date(2000, 0, 1, h).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    bHour.innerHTML = Array.from({ length: 24 }, (_, h) => `<option value="${h}">${esc(hourLabel(h))}</option>`).join('');
    const applyBrief = (b) => {
        bOn.checked = !!b.enabled;
        bHour.value = String(b.hour ?? 7);
        brief.querySelector('[data-brief-time]').hidden = !b.enabled;
        const n = getFollows().countries.length || b.countries || 0;
        bNote.textContent = b.enabled ? (n ? t('auth.briefingCount', { n }) : t('auth.briefingNone')) : '';
    };
    api('/me/briefing')
        .then((b) => {
            if (!b.available) return;
            applyBrief(b);
            brief.hidden = false;
        })
        .catch(() => {});
    const saveBrief = async () => {
        bOn.disabled = bHour.disabled = true;
        msg.textContent = '';
        try {
            applyBrief(await api('/me/briefing', { method: 'PUT', body: JSON.stringify({ enabled: bOn.checked, hour: Number(bHour.value), tz }) }));
            msg.textContent = t('auth.saved');
        } catch (err) {
            msg.textContent = err.message;
        } finally {
            bOn.disabled = bHour.disabled = false;
        }
    };
    bOn.addEventListener('change', saveBrief);
    bHour.addEventListener('change', saveBrief);

    // Email settings (hidden if the email list isn't connected).
    const box = dialog.querySelector('[data-email]');
    const upd = box.querySelector('[data-pref="updates"]');
    const news = box.querySelector('[data-pref="newsletter"]');
    const apply = (p) => {
        upd.checked = !!p.updates;
        news.checked = !!p.newsletter;
        news.disabled = !p.updates;
    };
    api('/me/email')
        .then((p) => {
            if (!p.available) return;
            apply(p);
            box.hidden = false;
        })
        .catch(() => {
            box.hidden = false;
            upd.disabled = news.disabled = true;
            msg.textContent = t('auth.prefsError');
        });
    const save = async () => {
        if (!upd.checked) news.checked = false;
        upd.disabled = news.disabled = true;
        msg.textContent = '';
        try {
            const p = await api('/me/email', { method: 'PUT', body: JSON.stringify({ updates: upd.checked, newsletter: news.checked }) });
            apply(p);
            msg.textContent = t('auth.saved');
        } catch (err) {
            msg.textContent = err.message;
        } finally {
            upd.disabled = false;
            news.disabled = !upd.checked;
        }
    };
    upd.addEventListener('change', save);
    news.addEventListener('change', save);

    dialog.querySelector('.au-logout').addEventListener('click', async () => {
        await api('/auth/logout', { method: 'POST' }).catch(() => {});
        me = null;
        renderButton();
        closeDialog();
    });
    dialog.querySelector('.au-delete').addEventListener('click', async () => {
        if (!confirm(t('auth.confirmDelete'))) return;
        try {
            await api('/me', { method: 'DELETE' });
            me = null;
            renderButton();
            openDialog(`<h2>${esc(t('auth.deletedTitle'))}</h2><p class="au-sub">${esc(t('auth.deletedSub'))}</p>`);
        } catch (err) {
            msg.textContent = err.message;
        }
    });
}

function scheduleSync() {
    if (!me) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
        api('/me/follows', { method: 'PUT', body: JSON.stringify(getFollows()) }).catch(() => {});
    }, 800);
}

// Open the sign-in window from other modules.
export function openSignIn(message = '') {
    if (dialog) showSignIn(message);
}

export async function initAuth() {
    const header = document.querySelector('.header-inner');
    if (header) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'auth-btn';
        btn.addEventListener('click', () => (me ? showAccount() : showSignIn()));
        header.appendChild(btn);
    }
    dialog = document.createElement('div');
    dialog.className = 'auth-dialog';
    dialog.addEventListener('click', (e) => e.target === dialog && closeDialog());
    document.body.appendChild(dialog);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && closeDialog());

    try {
        me = await api('/me');
        if (!me.signed_in) {
            if (me.auth_available === false) btn?.remove(); // email not set up yet
            throw new Error('signed out');
        }
        // Merge: anything followed on this device + anything saved on the account.
        const local = getFollows();
        const merged = {
            countries: [...new Set([...(me.follows?.countries || []), ...local.countries])],
            topics: [...new Set([...(me.follows?.topics || []), ...local.topics])],
        };
        setFollows(merged);
        scheduleSync();
        // Emails (sign-in, morning briefing) are written in the language this account uses on the site.
        if (me.lang !== lang) api('/me/lang', { method: 'PUT', body: JSON.stringify({ lang }) }).then(() => (me.lang = lang)).catch(() => {});
    } catch {
        me = null;
    }
    renderButton();
    onFollowsChange(scheduleSync);

    // Coming back from the email link: /app?signin=ok|used|expired|invalid
    const params = new URLSearchParams(location.search);
    const signin = params.get('signin');
    if (signin) {
        history.replaceState(null, '', location.pathname);
        if (signin === 'ok' && me) {
            openDialog(`<h2>${esc(t('auth.inTitle'))}</h2><p class="au-sub">${esc(t('auth.inSub', { email: me.email }))}</p>`);
        } else if (signin !== 'ok') {
            showSignIn(
                signin === 'expired' ? t('auth.expired') : t('auth.used'),
            );
        }
    }

    // "Morning briefing" in the Tools menu links to #briefing: the account window (sign-in first if needed).
    const openBriefing = () => {
        if (location.hash !== '#briefing') return;
        history.replaceState(null, '', location.pathname + location.search);
        if (me) {
            showAccount();
            dialog.querySelector('[data-briefing]')?.scrollIntoView({ block: 'center' });
        } else showSignIn(t('auth.briefingSub'));
    };
    openBriefing();
    window.addEventListener('hashchange', openBriefing);
}
